/** Reference catalogues. WEIGHTS carry CYC weight numbers, needle ranges and
 *  colloquial search aliases (Craft Yarn Council standard yarn-weight system). */

export interface Yarn {
  id: string;
  name: string;
  hex: string;
  custom?: boolean;
}
export interface Fiber {
  id: string;
  name: string;
  glyph: string;
  desc: string;
}
export interface Stitch {
  id: string;
  name: string;
  sym: string;
  short: string;
  desc: string;
}
export interface Weight {
  id: string;
  name: string;
  glyph: string;
  desc: string;
  /** stitch gauge sts/4in */
  g: number;
  /** row gauge rows/4in */
  rg: number;
  /** CYC weight number 0–7 */
  cyc: number;
  needle: string;
  aliases: string[];
}

export const YARNS: Yarn[] = [
  { id: 'cream', name: 'Cream', hex: '#F4EAD2' },
  { id: 'wheat', name: 'Wheat', hex: '#E3CC8F' },
  { id: 'mustard', name: 'Mustard', hex: '#D7A23B' },
  { id: 'amber', name: 'Amber', hex: '#E58E3A' },
  { id: 'pumpkin', name: 'Pumpkin', hex: '#C9622C' },
  { id: 'rust', name: 'Rust', hex: '#A8472A' },
  { id: 'brick', name: 'Brick', hex: '#7E3528' },
  { id: 'rose', name: 'Rosehip', hex: '#C46A78' },
  { id: 'berry', name: 'Berry', hex: '#94365A' },
  { id: 'plum', name: 'Plum', hex: '#5E3357' },
  { id: 'indigo', name: 'Indigo', hex: '#35406E' },
  { id: 'steel', name: 'Steel', hex: '#3C5670' },
  { id: 'teal', name: 'Teal', hex: '#2C6E69' },
  { id: 'pine', name: 'Pine', hex: '#2E5A40' },
  { id: 'stone', name: 'Stone', hex: '#9C9080' },
  { id: 'char', name: 'Charcoal', hex: '#2C2825' },
];

export const FIBERS: Fiber[] = [
  { id: 'merino', name: 'Merino wool', glyph: 'M', desc: 'soft · springy · warm' },
  { id: 'alpaca', name: 'Alpaca', glyph: 'A', desc: 'silky · drapey · toasty' },
  { id: 'cotton', name: 'Pima cotton', glyph: 'C', desc: 'crisp · cool · no stretch' },
  { id: 'cash', name: 'Cashmere', glyph: 'K', desc: 'luxe · plush · delicate' },
  { id: 'mohair', name: 'Kid mohair', glyph: 'H', desc: 'haloed · airy · light' },
  { id: 'bamboo', name: 'Bamboo viscose', glyph: 'B', desc: 'smooth · sheen · fluid' },
  { id: 'linen', name: 'Linen', glyph: 'L', desc: 'rustic · structured · cool' },
  { id: 'silk', name: 'Silk blend', glyph: 'S', desc: 'lustrous · strong · slick' },
  { id: 'tweed', name: 'Donegal tweed', glyph: 'T', desc: 'flecked · hearty · woolly' },
  { id: 'acryl', name: 'Acrylic', glyph: 'Y', desc: 'washable · sturdy · thrifty' },
];

export const STITCHES: Stitch[] = [
  { id: 'knit', name: 'Knit', sym: '', short: 'K', desc: 'smooth V on the right side' },
  { id: 'purl', name: 'Purl', sym: '•', short: 'P', desc: 'bump on the right side' },
  { id: 'yo', name: 'Yarn over', sym: '○', short: 'YO', desc: 'eyelet increase' },
  { id: 'k2tog', name: 'K2tog', sym: '/', short: 'K2tog', desc: 'right-leaning decrease' },
  { id: 'ssk', name: 'SSK', sym: '\\', short: 'SSK', desc: 'left-leaning decrease' },
  { id: 'cable', name: 'Cable 4 front', sym: '✕', short: 'C4F', desc: '4-st left cross' },
  { id: 'bob', name: 'Bobble', sym: '●', short: 'MB', desc: 'raised cluster' },
  { id: 'slip', name: 'Slip', sym: 'v', short: 'Sl', desc: 'pass without working' },
];

export const WEIGHTS: Weight[] = [
  { id: 'lace', name: 'Lace', glyph: '0', desc: '≈ 33 sts / 4in', g: 33, rg: 44, cyc: 0, needle: '000–1', aliases: ['lace', 'cobweb', 'thread'] },
  { id: 'fing', name: 'Fingering', glyph: '1', desc: '≈ 28 sts / 4in', g: 28, rg: 38, cyc: 1, needle: '1–3', aliases: ['fingering', 'sock', 'baby', 'super fine'] },
  { id: 'sport', name: 'Sport', glyph: '2', desc: '≈ 26 sts / 4in', g: 26, rg: 36, cyc: 2, needle: '3–5', aliases: ['sport', 'fine'] },
  { id: 'dk', name: 'DK', glyph: '3', desc: '≈ 22 sts / 4in', g: 22, rg: 30, cyc: 3, needle: '5–7', aliases: ['dk', 'double knitting', 'light worsted'] },
  { id: 'wor', name: 'Worsted', glyph: '4', desc: '≈ 20 sts / 4in', g: 20, rg: 26, cyc: 4, needle: '7–9', aliases: ['worsted', 'afghan', 'medium'] },
  { id: 'aran', name: 'Aran', glyph: '5', desc: '≈ 18 sts / 4in', g: 18, rg: 24, cyc: 4, needle: '8–10', aliases: ['aran', 'heavy worsted'] },
  { id: 'bulky', name: 'Bulky', glyph: '6', desc: '≈ 14 sts / 4in', g: 14, rg: 19, cyc: 5, needle: '9–11', aliases: ['bulky', 'chunky', 'craft'] },
  { id: 'sbulky', name: 'Super Bulky', glyph: '7', desc: '≈ 9 sts / 4in', g: 9, rg: 12, cyc: 6, needle: '11–17', aliases: ['super bulky', 'roving'] },
];

const WEIGHT_ALIAS: Record<string, string> = {
  lace: 'lace', cobweb: 'lace', fingering: 'fing', fing: 'fing', sock: 'fing', superfine: 'fing',
  sport: 'sport', fine: 'sport', dk: 'dk', light: 'dk', worsted: 'wor', wor: 'wor', medium: 'wor',
  aran: 'aran', bulky: 'bulky', chunky: 'bulky', superbulky: 'sbulky', sbulky: 'sbulky', jumbo: 'sbulky',
};

export const yarnById = (id: string | null | undefined, extra: Yarn[] = []) =>
  [...YARNS, ...extra].find((y) => y.id === id);
export const fiberById = (id: string) => FIBERS.find((f) => f.id === id);
export const stitchById = (id: string) => STITCHES.find((s) => s.id === id);
export const weightById = (id: string) => WEIGHTS.find((w) => w.id === id);

export function resolveWeightId(w: string | null | undefined): string | null {
  if (!w) return null;
  const k = String(w).toLowerCase().replace(/[^a-z]/g, '');
  return WEIGHT_ALIAS[k] ?? (WEIGHTS.some((x) => x.id === k) ? k : null);
}

export function luminance(hex: string): number {
  const m = hex.replace('#', '');
  const r = parseInt(m.substr(0, 2), 16),
    g = parseInt(m.substr(2, 2), 16),
    b = parseInt(m.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
export const isDark = (hex: string) => luminance(hex) < 0.55;
